import intersection from "lodash.intersection";
import ezStore, { useStoreState, Session } from "ezStore";
import { UserDetails } from "@cofano/coastguard2-frontend-client";
import { PluginFields } from "types/enums";
import { Permission } from "__generated__/enums";

export const Fields = {
	ADDRESS: "address",
	CALL: "call",
	CARGO: "cargo",
	CARGO_ACTION: "cargoAction",
	CONTRACT: "contract",
	LOCATION: "location",
	ORDER: "order",
	PRODUCT: "product",
	USER: "user",
	RELATION: "relation",
	SHIP: "ship",
	VOYAGE: "voyage",
	SALES_CHARTER: "salesCharter",
};

export const Plugin = {
	SHIP_ACTIONS: "shipActions",
	USE_CONTAINERS: "useContainers",
	SKIPPER_DASHBOARD: "skipperDashboard",
};

export const usePermission = () => {
	const session = useStoreState(store => store.session);

	return {
		isCofanoAdmin: isCofanoAdmin(session),
		isAdmin: isAdmin(session),
		isCompanyUser: isCompanyUser(session),
		isCustomer: isCustomer(session),
		isShipManager: isShipManager(session),
		isSkipper: isSkipper(session),
		isSkipperOrShipManager: isSkipper(session) || isShipManager(session),
		isOperator: isOperator(session),
		isTerminal: isTerminal(session),
		isMasterdata: isMasterdata(session),
		isFinance: isFinance(session),
		isMainUserLimited: isMainUserLimited(session),
		isReadOnly: isReadOnly(session),
		isFinanceAdmin: isFinanceAdmin(session),
		isRTR: isRTR(session),
		isTankmatch: isTankmatch(session),
	};
};

export const Authority = {
	APPLICATION_MAIN_USER: "application/OP_MAIN_USER",
	APPLICATION_ADMINISTRATOR: "application/OP_ADMINISTRATOR",
	APPLICATION_MAIN: "application/OP_MAIN",
	APPLICATION_MAIN_LIMITED: "application/OP_MAIN_LIMITED",
	APPLICATION_MASTERDATA: "application/OP_MASTERDATA",
	APPLICATION_FINANCE: "application/OP_FINANCE",
	APPLICATION_FINANCE_ADMIN: "application/OP_FINANCE_ADMINISTRATOR",
	APPLICATION_OPERATOR: "application/OP_OPERATOR",
	APPLICATION_SHIP_MANAGER: "application/OP_SHIP_MANAGER",
	APPLICATION_SKIPPER: "application/OP_SKIPPER",
	APPLICATION_CUSTOMER: "application/OP_CUSTOMER",
	APPLICATION_TERMINAL: "application/OP_TERMINAL",
	SUB_APPLICATION_OPERATOR: "subApplication/OP_OPERATOR",
	SUB_APPLICATION_SHIP_MANAGER: "subApplication/OP_SHIP_MANAGER",
	SUB_APPLICATION_SKIPPER: "subApplication/OP_SKIPPER",
	SUB_APPLICATION_CUSTOMER: "subApplication/OP_CUSTOMER",
	SUB_APPLICATION_TERMINAL: "subApplication/OP_TERMINAL",
	APPLICATION_READONLY: "application/OP_READ_ONLY",
};

export function hasAnyAuthority(userDetails: UserDetails, authorities: string[]) {
	return userDetails != null && authorities != null && intersection(userDetails.authorities, authorities).length > 0;
}

export function hasAuthority(userDetails: UserDetails, authority: string) {
	return authority != null && hasAnyAuthority(userDetails, [ authority ]);
}

export const isCofanoAdmin = (session: Session) => {
	return session.user != null
		&& hasAuthority(session.user, Authority.APPLICATION_ADMINISTRATOR);
};

export const isAdmin = (session: Session) => {
	return session.user != null
		&& hasAuthority(session.user, Authority.APPLICATION_MAIN_USER)
		&& hasAuthority(session.user, Authority.APPLICATION_ADMINISTRATOR);
};

export const isCompanyUser = (session: Session) => {
	return session.user != null
		&& hasAuthority(session.user, Authority.APPLICATION_MAIN_USER)
		&& hasAuthority(session.user, Authority.APPLICATION_MAIN);
};

export const isReadOnly = (session: Session) => {
	return session.user == null
		|| hasAuthority(session.user, Authority.APPLICATION_READONLY);
};

export const isRTR = (session: Session) => {
	return isAdmin(session) || isCompanyUser(session) || isMainUserLimited(session);
};

export const isMainUserLimited = (session: Session) => {
	return session.user != null
		&& hasAuthority(session.user, Authority.APPLICATION_MAIN_USER)
		&& hasAuthority(session.user, Authority.APPLICATION_MAIN_LIMITED);
};

export const isMasterdata = (session: Session) => {
	return session.user != null
		&& hasAuthority(session.user, Authority.APPLICATION_MASTERDATA);
};

export const isFinance = (session: Session) => {
	return session.user != null
		&& hasAuthority(session.user, Authority.APPLICATION_MAIN_USER)
		&& (
			hasAuthority(session.user, Authority.APPLICATION_FINANCE) ||
			hasAuthority(session.user, Authority.APPLICATION_FINANCE_ADMIN)
		);
};

export const isCustomer = (session: Session) => {
	return session.user != null
		&& hasAnyAuthority(session.user, [ Authority.APPLICATION_CUSTOMER, Authority.SUB_APPLICATION_CUSTOMER ]);
};

export const isOperator = (session: Session) => {
	return session.user != null
		&& hasAnyAuthority(session.user, [ Authority.APPLICATION_OPERATOR, Authority.SUB_APPLICATION_OPERATOR ]);
};

export const isShipManager = (session: Session) => {
	return session.user != null
		&& hasAnyAuthority(session.user, [ Authority.APPLICATION_SHIP_MANAGER, Authority.SUB_APPLICATION_SHIP_MANAGER ]);
};

export const isSkipper = (session: Session) => {
	return session.user != null
		&& hasAnyAuthority(session.user, [ Authority.APPLICATION_SKIPPER, Authority.SUB_APPLICATION_SKIPPER ]);
};

export const isTerminal = (session: Session) => {
	return session.user != null
		&& hasAnyAuthority(session.user, [ Authority.APPLICATION_TERMINAL, Authority.SUB_APPLICATION_TERMINAL ]);
};

export const isFinanceAdmin = (session: Session) => {
	return session.user != null
		&& hasAuthority(session.user, Authority.APPLICATION_MAIN_USER)
		&& hasAuthority(session.user, Authority.APPLICATION_FINANCE_ADMIN);
};

export const getPermission = (session: Session): Permission | null => {
	if (isAdmin(session)) {
		return Permission.Administrator;
	} else if (isCompanyUser(session)) {
		return Permission.Main;
	} else if (isMainUserLimited(session)) {
		return Permission.MainUserLimited;
	} else if (isMasterdata(session)) {
		return Permission.Masterdata;
	} else if (isFinanceAdmin(session)) {
		return Permission.FinanceAdministrator;
	} else if (isFinance(session)) {
		return Permission.Finance;
	} else if (isOperator(session)) {
		return Permission.Operator;
	} else if (isShipManager(session)) {
		return Permission.ShipManager;
	} else if (isSkipper(session)) {
		return Permission.Skipper;
	} else if (isCustomer(session)) {
		return Permission.Customer;
	} else if (isTerminal(session)) {
		return Permission.Terminal;
	} else {
		return null;
	}
};

export const hasPluginPermission = ({ settings }: Session, plugin: string) => {
	const enabledPlugins = settings?.plugins;
	return !!enabledPlugins?.[plugin];
};

export const hasOptionalFieldPermission = ({ settings }: Session, entity: PluginFields | string, field: string) => {
	const fields = settings?.fields;
	return fields?.[entity as PluginFields]?.includes(field);
};

export const getPluginSettingValue = ({ settings }: Session, pluginSetting: string) => {
	const pluginSettings = settings?.settings;
	return pluginSettings?.[pluginSetting];
};

export const isTankmatch = (session: Session = ezStore.getState().session) => {
	const customer = getPluginSettingValue(session, "customer") || "";
	return customer.startsWith("Tankmatch");
};

export const getMultiValuesField = ({ settings }: Session, field: string) => {
	const multiValues = settings?.multiValues;
	return multiValues?.[field] || "";
};
